import {
  Redirect,
  Route,
  BrowserRouter as Router,
  Switch,
} from "react-router-dom";
import { Suspense, lazy, useEffect } from "react";
import { persistor, store } from "./api/store";

import Dashboard from "pages/Dashboard";
import Loader from "components/global/Loader";
import IntercomManager from "components/global/IntercomManager";
import { PersistGate } from "redux-persist/integration/react";
import { Provider } from "react-redux";
import { authService } from "api";
import Login from "views/onboarding/Login";
import AuthRedirect from "pages/AuthRedirect";
import HelpCenter from "views/HelpCenter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import MobileRedirect from "pages/MobileRedirect";
import RegisterWithSocials from "views/onboarding/RegisterWithSocials";
import LoginWithCode from "views/onboarding/LoginWithCode";
import Register from "views/onboarding/Register";
import ForgotPassword from "pages/ForgotPassword";
import VerifyEmail from "views/onboarding/VerifyEmailNew";
import Onboarding from "views/onboarding/Onboarding";
import RegisterWithEmail from "views/onboarding/RegisterWithEmail";
import LoginWithEmail from "views/onboarding/LoginWithEmail";
import SelectChannel from "pages/SelectAccount";
import { lazyWithReload } from "components/global/LazyRetry";

const queryClient = new QueryClient();

// Pages
const PageNotFound = lazy(() =>
  lazyWithReload(() => import("pages/PageNotFound"), "page-not-found")
);
const AffiliateEarningCalculator = lazy(() =>
  lazyWithReload(
    () => import("pages/AffiliateEarningCalculator"),
    "affiliate-earning-calculator"
  )
);
const UnsubscribeIndex = lazy(() =>
  lazyWithReload(
    () => import("pages/unsubscribe/UnsubscribeIndex"),
    "unsubscribe-index"
  )
);
const PlannerAccessCode = lazy(() =>
  lazyWithReload(
    () => import("views/postPlanner/clientView/AccessCodePage"),
    "planner-access-code"
  )
);
const ClientDraftPage = lazy(() =>
  lazyWithReload(
    () => import("views/drafts/clientView/DraftPage"),
    "client-draft-page"
  )
);
const Planner = lazy(() =>
  lazyWithReload(
    () => import("views/postPlanner/clientView/ClientViewIndex"),
    "planner-client-view"
  )
);
const MiniAppHome = lazy(() =>
  lazyWithReload(() => import("views/miniApps/MiniAppsRoute"), "mini-app-home")
);
// Axios interceptors
authService();

function App() {
  useEffect(() => {
    const mobileUrl = window.location.search;
    if (mobileUrl.includes("postpaddy-mobile")) {
      localStorage.setItem("mobileUrl", "postpaddy-mobile");
    }
  }, []);
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <QueryClientProvider client={queryClient}>
          <Suspense fallback={<Loader className="cover" />}>
            <Router>
              <div className="App">
                <HelpCenter />
                <IntercomManager />
                <Switch>
                  <Route
                    exact
                    path="/"
                    component={() => <Redirect to="/dashboard" />}
                  />
                  <Route exact path="/sign-in" component={Login} />
                  <Route
                    exact
                    path="/sign-in/email"
                    component={LoginWithEmail}
                  />
                  <Route
                    exact
                    path="/sign-in/with-code"
                    component={LoginWithCode}
                  />
                  <Route exact path="/sign-up" component={Register} />
                  <Route
                    exact
                    path="/sign-up/email"
                    component={RegisterWithEmail}
                  />
                  <Route
                    exact
                    path="/sign-up/:social"
                    component={RegisterWithSocials}
                  />
                  <Route
                    exact
                    path="/auth/:provider/callback"
                    component={AuthRedirect}
                  />
                  <Route
                    exact
                    path="/auth/select-channel/:platform"
                    component={SelectChannel}
                  />
                  <Route
                    exact
                    path="/mobile-connect-channel/redirectmobile"
                    component={MobileRedirect}
                  />
                  <Route path="/password-reset" component={ForgotPassword} />
                  <Route exact path="/verify-email" component={VerifyEmail} />
                  <Route path="/dashboard" component={Dashboard} />
                  <Route
                    path="/affiliate-earning-calculator"
                    component={AffiliateEarningCalculator}
                  />
                  <Route path="/onboarding" component={Onboarding} />
                  <Route path="/unsubscribe" component={UnsubscribeIndex} />
                  <Route path="/verify-client" component={PlannerAccessCode} />
                  <Route
                    path="/draft/:draftId/shared"
                    component={ClientDraftPage}
                  />
                  <Route path="/planner/:plannerId/view" component={Planner} />
                  <Route path="/mini-apps" component={MiniAppHome} />

                  {/* No match */}
                  <Route component={PageNotFound} />
                </Switch>
              </div>
            </Router>
          </Suspense>
        </QueryClientProvider>
      </PersistGate>
    </Provider>
  );
}

export default App;
